import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useAuth } from '@/stores/auth';
import { useLock } from '@/stores/lock';
import { usePreferences } from '@/stores/preferences';
import { useShellNav } from '@/stores/shellNav';
import { useShellUi } from '@/stores/shellUi';
import { openSettingsPage, visibleSections } from '@/screens/settings/pages';
import { signOutSafely } from './actions';
import { MODULES, canSee } from './modules';

interface Command {
  id: string;
  label: string;
  /** Where it lives, shown on the right: the module, or "Command". */
  group: string;
  run: () => void;
}

/**
 * Ctrl+K: type a few letters of any screen or command and press Enter. The
 * fastest way around the app for someone who knows what they want, and a
 * way to find a screen without knowing which module holds it.
 */
export function CommandPalette() {
  const close = useShellUi((s) => s.close);
  const openDialog = useShellUi((s) => s.open);
  const navigate = useShellNav((s) => s.navigate);
  const can = useAuth((s) => s.can);
  const lock = useLock((s) => s.lock);
  const prefs = usePreferences((s) => s.prefs);
  const setPrefs = usePreferences((s) => s.set);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commands = useMemo<Command[]>(() => {
    const run = (fn: () => void) => () => {
      close();
      fn();
    };
    const screens: Command[] = MODULES.flatMap((m) =>
      m.screens
        .filter((s) => s.built && canSee(s, can))
        .map((s) => ({ id: `screen:${s.id}`, label: s.label, group: m.label, run: run(() => navigate(s.id)) })),
    );
    const settings: Command[] = visibleSections(can).map((p) => ({
      id: `settings:${p.id}`,
      label: `Settings: ${p.label}`,
      group: 'Settings',
      run: run(() => openSettingsPage(p.id, navigate)),
    }));
    const actions: Command[] = [
      { id: 'help', label: 'Help', group: 'Command', run: run(() => openDialog('help')) },
      { id: 'lock', label: 'Lock the screen', group: 'Command', run: run(lock) },
      {
        id: 'contrast',
        label: prefs.highContrast ? 'Turn off high contrast' : 'Turn on high contrast',
        group: 'Command',
        run: run(() => setPrefs({ highContrast: !prefs.highContrast })),
      },
      {
        id: 'motion',
        label: prefs.reducedMotion ? 'Allow movement again' : 'Less movement',
        group: 'Command',
        run: run(() => setPrefs({ reducedMotion: !prefs.reducedMotion })),
      },
      { id: 'signout', label: 'Sign out', group: 'Command', run: run(() => void signOutSafely()) },
    ];
    return [...screens, ...settings, ...actions];
  }, [can, close, lock, navigate, openDialog, prefs.highContrast, prefs.reducedMotion, setPrefs]);

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = words.length
    ? commands.filter((c) => words.every((w) => `${c.label} ${c.group}`.toLowerCase().includes(w)))
    : commands;
  const current = Math.min(active, Math.max(0, shown.length - 1));

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') setActive((current + 1) % Math.max(1, shown.length));
    else if (e.key === 'ArrowUp') setActive((current - 1 + shown.length) % Math.max(1, shown.length));
    else if (e.key === 'Enter') shown[current]?.run();
    else if (e.key === 'Escape') close();
    else return;
    e.preventDefault();
  }

  return (
    <div className="palette__overlay" onClick={close}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Find a screen or command"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="palette__input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Type a screen or command…"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          aria-activedescendant={shown[current] ? `palette-${shown[current].id}` : undefined}
          aria-label="Find a screen or command"
        />
        <ul className="palette__list" id="palette-list" role="listbox">
          {shown.length === 0 && <li className="palette__empty">Nothing matches. Try a shorter word.</li>}
          {shown.map((c, i) => (
            <li
              key={c.id}
              id={`palette-${c.id}`}
              role="option"
              aria-selected={i === current}
              className={`palette__item${i === current ? ' palette__item--active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={c.run}
            >
              <span>{c.label}</span>
              <span className="palette__group">{c.group}</span>
            </li>
          ))}
        </ul>
        <p className="palette__hint">↑ ↓ to choose · Enter to open · Esc to close</p>
      </div>
    </div>
  );
}
